import type {Bar} from '../../util/plotHelpers/types';
import {compareArtifactVersionMetricRowsByVersion} from './artifactVersionMetricChartUtils';
import {getArtifactVersionMetricColor} from './artifactVersionMetricColors';
import type {ArtifactVersionMetricChartRow} from './types';

type ArtifactVersionMetricNumericChartRow = ArtifactVersionMetricChartRow & {
  value: number;
};

export type ArtifactVersionMetricBar = Bar & {
  artifactRow: ArtifactVersionMetricChartRow;
};

export type ArtifactVersionMetricPlotPoint = {
  x: number;
  y: number;
  color: string;
  uniqueId: string;
  artifactRow: ArtifactVersionMetricChartRow;
  xMetricValue?: number;
};

function hasNumericArtifactVersionMetricValue(
  row: ArtifactVersionMetricChartRow
): row is ArtifactVersionMetricNumericChartRow {
  // Enforce finiteness at the adapter boundary so non-finite values can never
  // reach plot coordinates, independent of how upstream rows were produced.
  return row.value != null && Number.isFinite(row.value);
}

export function toArtifactVersionMetricScatterPoints(
  yRows: ArtifactVersionMetricChartRow[],
  xRows: ArtifactVersionMetricChartRow[]
): ArtifactVersionMetricPlotPoint[] {
  const xValueByMembershipId = new Map(
    xRows
      .filter(hasNumericArtifactVersionMetricValue)
      .map(row => [row.membershipId, row.value])
  );

  return [...yRows]
    .filter(
      (row): row is ArtifactVersionMetricNumericChartRow =>
        hasNumericArtifactVersionMetricValue(row) &&
        xValueByMembershipId.has(row.membershipId)
    )
    .sort(compareArtifactVersionMetricRowsByVersion)
    .map(row => {
      const x = xValueByMembershipId.get(row.membershipId)!;
      return {
        x,
        y: row.value,
        color: getArtifactVersionMetricColor(row),
        uniqueId: row.membershipId,
        artifactRow: row,
        xMetricValue: x,
      };
    });
}

export function toArtifactVersionMetricBars(
  rows: ArtifactVersionMetricChartRow[]
): ArtifactVersionMetricBar[] {
  return [...rows]
    .filter(hasNumericArtifactVersionMetricValue)
    .sort(compareArtifactVersionMetricRowsByVersion)
    .map(row => ({
      key: row.label,
      value: row.value,
      color: getArtifactVersionMetricColor(row),
      uniqueId: row.membershipId,
      title: row.label,
      displayName: row.label,
      artifactRow: row,
    }));
}

export function toArtifactVersionMetricPlotPoints(
  rows: ArtifactVersionMetricChartRow[]
): ArtifactVersionMetricPlotPoint[] {
  return [...rows]
    .sort(compareArtifactVersionMetricRowsByVersion)
    .flatMap((row, index) =>
      hasNumericArtifactVersionMetricValue(row)
        ? [
            {
              x: index,
              y: row.value,
              color: getArtifactVersionMetricColor(row),
              uniqueId: row.membershipId,
              artifactRow: row,
            },
          ]
        : []
    );
}

export function toArtifactVersionMetricLineSegments(
  rows: ArtifactVersionMetricChartRow[]
): Array<Array<{x: number; y: number}>> {
  const segments: Array<Array<{x: number; y: number}>> = [];
  let currentSegment: Array<{x: number; y: number}> = [];

  for (const [index, row] of [...rows]
    .sort(compareArtifactVersionMetricRowsByVersion)
    .entries()) {
    if (!hasNumericArtifactVersionMetricValue(row)) {
      if (currentSegment.length > 0) {
        segments.push(currentSegment);
        currentSegment = [];
      }
      continue;
    }

    currentSegment.push({
      x: index,
      y: row.value,
    });
  }

  if (currentSegment.length > 0) {
    segments.push(currentSegment);
  }

  return segments;
}
